/**
 * Zustand Admin Store (AC-920)
 *
 * Manages admin portal state including:
 * - Email allowlist (CRUD, search, pagination)
 * - Registration mode (open / invite_only)
 * - Admin user management (list, promote, demote)
 *
 * Epic: AC-915 - Soft-Launch Email Allowlist with Admin Portal
 */

import { create } from 'zustand';
import { apiService } from '../services/api';
import { AllowlistEntry, AdminUserSummary } from '../types';

interface AdminState {
  // Allowlist state
  allowlist: AllowlistEntry[];
  allowlistTotal: number;
  allowlistLoading: boolean;
  allowlistError: string | null;

  // Registration mode state
  registrationMode: 'open' | 'invite_only';
  registrationModeLoading: boolean;

  // Users state
  users: AdminUserSummary[];
  usersTotal: number;
  usersLoading: boolean;
  usersError: string | null;

  // Allowlist actions
  fetchAllowlist: (params?: { search?: string; skip?: number; limit?: number }) => Promise<void>;
  addEmail: (email: string) => Promise<void>;
  addEmails: (emails: string[]) => Promise<{ added: number; duplicates: number; entries: AllowlistEntry[] }>;
  removeEmail: (entryId: number) => Promise<void>;

  // Registration mode actions
  fetchRegistrationMode: () => Promise<void>;
  setRegistrationMode: (mode: 'open' | 'invite_only') => Promise<void>;

  // Users actions
  fetchUsers: (params?: { search?: string; is_admin?: boolean; skip?: number; limit?: number }) => Promise<void>;
  setUserRole: (userId: number, isAdmin: boolean) => Promise<void>;
  deleteUser: (userId: number) => Promise<void>;
}

export const useAdminStore = create<AdminState>((set, get) => ({
  // Initial allowlist state
  allowlist: [],
  allowlistTotal: 0,
  allowlistLoading: false,
  allowlistError: null,

  // Initial registration mode state
  registrationMode: 'open',
  registrationModeLoading: false,

  // Initial users state
  users: [],
  usersTotal: 0,
  usersLoading: false,
  usersError: null,

  // Allowlist actions
  fetchAllowlist: async (params) => {
    set({ allowlistLoading: true, allowlistError: null });
    try {
      const response = await apiService.getAllowlist(params);
      set({
        allowlist: response.entries,
        allowlistTotal: response.total,
        allowlistLoading: false,
      });
    } catch (error: any) {
      set({
        allowlistLoading: false,
        allowlistError: error.message || 'Failed to fetch allowlist',
      });
    }
  },

  addEmail: async (email: string) => {
    try {
      const newEntry = await apiService.addAllowlistEmail(email);
      set((state) => {
        // Avoid duplicates: only append if entry doesn't already exist
        const exists = state.allowlist.some((e) => e.id === newEntry.id);
        if (exists) return state;
        return {
          allowlist: [...state.allowlist, newEntry],
          allowlistTotal: state.allowlistTotal + 1,
        };
      });
    } catch (error) {
      throw error;
    }
  },

  addEmails: async (emails: string[]) => {
    const response = await apiService.addAllowlistEmailsBulk(emails);
    set((state) => ({
      allowlist: [...state.allowlist, ...response.entries],
      allowlistTotal: state.allowlistTotal + response.added,
    }));
    return response;
  },

  removeEmail: async (entryId: number) => {
    await apiService.removeAllowlistEmail(entryId);
    set((state) => ({
      allowlist: state.allowlist.filter((entry) => entry.id !== entryId),
      allowlistTotal: state.allowlistTotal - 1,
    }));
  },

  // Registration mode actions
  fetchRegistrationMode: async () => {
    set({ registrationModeLoading: true });
    try {
      const response = await apiService.getRegistrationMode();
      set({
        registrationMode: response.mode as 'open' | 'invite_only',
        registrationModeLoading: false,
      });
    } catch (error) {
      set({ registrationModeLoading: false });
    }
  },

  setRegistrationMode: async (mode: 'open' | 'invite_only') => {
    set({ registrationModeLoading: true });
    try {
      const response = await apiService.setRegistrationMode(mode);
      set({
        registrationMode: response.mode as 'open' | 'invite_only',
        registrationModeLoading: false,
      });
    } catch (error) {
      set({ registrationModeLoading: false });
      throw error;
    }
  },

  // Users actions
  fetchUsers: async (params) => {
    set({ usersLoading: true, usersError: null });
    try {
      const response = await apiService.getAdminUsers(params);
      set({
        users: response.users,
        usersTotal: response.total,
        usersLoading: false,
      });
    } catch (error: any) {
      set({
        usersLoading: false,
        usersError: error.message || 'Failed to fetch users',
      });
    }
  },

  setUserRole: async (userId: number, isAdmin: boolean) => {
    const updatedUser = await apiService.setAdminUserRole(userId, isAdmin);
    set((state) => ({
      users: state.users.map((user) =>
        user.user_id === userId ? { ...user, is_admin: updatedUser.is_admin } : user
      ),
    }));
  },

  deleteUser: async (userId: number) => {
    await apiService.deleteAdminUser(userId);
    set((state) => ({
      users: state.users.filter((user) => user.user_id !== userId),
      usersTotal: state.usersTotal - 1,
    }));
  },
}));

export default useAdminStore;
